import { toast } from 'react-toastify';

import * as Sentry from '@sentry/react';
import { ModelError } from '@spazious/client-backoffice';

interface AxiosErrorLike {
  isAxiosError: true;
  response?: {
    status?: number;
    data?: {
      errors?: ModelError[];
    };
  };
}

const isAxiosErrorLike = (error: unknown): error is AxiosErrorLike =>
  typeof error === 'object' &&
  error !== null &&
  (error as { isAxiosError?: unknown }).isAxiosError === true;

const handleRequestError = (error: unknown): void => {
  // Every failure is reported, 4xx included: a 4xx is usually the backend
  // answering the user about their own input, but a malformed JSON:API `type`,
  // an id sent as a number and a stale `available_transitions` left by a missing
  // invalidation all surface as one too, and nothing else catches those in
  // production. Filtering by status here would hide them; how loud an event is
  // belongs to the triage layer, where `describeApiError` demotes an outcome the
  // backend answered on purpose to `warning` and skips its replay (SPAZ2-3520).
  //
  // Reporting used to happen by accident: nothing caught the rejected mutation
  // promise, so a failure arrived as an unhandled rejection — and conversely,
  // anything a migrated hook swallowed (`useSwallowedMutation`) was never
  // reported at all, 5xx included. This is the one place that reports, and it
  // covers every consumer of this handler rather than one submit (SPAZ2-3507).
  //
  // Cancelled requests, a confirmed-offline browser and the 401 that completes a
  // logout are dropped by `spaziousContextIntegration`, so they are not filtered
  // again here.
  Sentry.captureException(error);

  if (!isAxiosErrorLike(error)) {
    toast.error('Something went wrong');
    return;
  }
  if (error.response?.status === 403) {
    toast.error('You do not have permissions to perform this action');
    return;
  }
  const errors = error.response?.data?.errors ?? [];
  if (errors.length) {
    errors.forEach((errorMessage) => {
      toast.error(errorMessage.detail);
    });
  } else {
    toast.error('Something went wrong');
  }
};
export default handleRequestError;
